import { useCallback, useEffect, useRef, useState } from "react";
import type { QuestionnaireSummary } from "@ferrivia/api-contract";
import { useApi } from "@ferrivia/auth-web";

export type QuestionnaireSummaryState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; summary: QuestionnaireSummary };

export interface UseQuestionnaireSummaryResult {
  state: QuestionnaireSummaryState;
  refetch: () => void;
}

export function useQuestionnaireSummary(id: string): UseQuestionnaireSummaryResult {
  const api = useApi();
  const [state, setState] = useState<QuestionnaireSummaryState>({ status: "loading" });
  const cancelledRef = useRef(false);

  const fetchSummary = useCallback(() => {
    setState({ status: "loading" });
    api.questionnaireSummaries
      .get(id)
      .then((summary) => {
        if (!cancelledRef.current) {
          setState({ status: "success", summary });
        }
      })
      .catch((err: unknown) => {
        if (!cancelledRef.current) {
          setState({ status: "error", error: err instanceof Error ? err.message : "Failed to load summary" });
        }
      });
  }, [api, id]);

  useEffect(() => {
    cancelledRef.current = false;
    fetchSummary();
    return () => {
      cancelledRef.current = true;
    };
  }, [fetchSummary]);

  return { state, refetch: fetchSummary };
}
